import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Eye,
  ThumbsUp,
  MessageCircle,
  Sparkles,
  Flame,
  AlertTriangle,
  HelpCircle,
  Lightbulb,
} from "lucide-react";
import { api } from "@/lib/api";
import type { SyncSuggestions, SyncedPost, PlatformAccount } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { SyncControls } from "@/components/shared/SyncControls";
import { Button } from "@/components/ui/button";

const nFmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K`
    : String(n);

const IntentPill: React.FC<{ label: string; count: number; tone: string }> = ({ label, count, tone }) => (
  <span className={`sg-chip ${tone}`} data-testid={`intent-pill-${label}`}>
    {label} · {count}
  </span>
);

const VideoRow: React.FC<{ v: SyncedPost }> = ({ v }) => (
  <a
    href={v.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex gap-3 py-3 border-b border-[color:var(--sg-border)] hover:bg-slate-50/60 transition-colors rounded-md px-2"
    data-testid={`synced-video-${v.external_id}`}
  >
    {v.thumbnail_url ? (
      <img
        src={v.thumbnail_url}
        alt=""
        loading="lazy"
        decoding="async"
        className="w-32 aspect-video object-cover rounded-md flex-shrink-0"
      />
    ) : (
      <div className="w-32 aspect-video bg-slate-200 rounded-md flex-shrink-0" />
    )}
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold line-clamp-2">{v.title}</div>
      <div className="text-xs text-[color:var(--sg-muted)] mt-1">
        {v.published_at ? new Date(v.published_at).toLocaleDateString() : ""}
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs">
        <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {nFmt(v.view_count)}</span>
        <span className="inline-flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> {nFmt(v.like_count)}</span>
        <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {nFmt(v.comment_count)}</span>
      </div>
      {v.intent_breakdown && Object.keys(v.intent_breakdown).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {v.intent_breakdown["buying_intent"] ? (
            <IntentPill label="buying" count={v.intent_breakdown["buying_intent"]} tone="bg-emerald-50 text-emerald-700 border-emerald-200" />
          ) : null}
          {v.intent_breakdown["product_question"] ? (
            <IntentPill label="questions" count={v.intent_breakdown["product_question"]} tone="bg-sky-50 text-sky-700 border-sky-200" />
          ) : null}
          {v.intent_breakdown["complaint"] ? (
            <IntentPill label="complaints" count={v.intent_breakdown["complaint"]} tone="bg-rose-50 text-rose-700 border-rose-200" />
          ) : null}
        </div>
      )}
    </div>
  </a>
);

export const ChannelInsights: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();

  const account = useQuery<PlatformAccount[]>({
    queryKey: ["platforms"],
    queryFn: async () => (await api.get("/platforms")).data,
    enabled: !!accountId,
    select: (list) => list,
  });
  const currentAccount = account.data?.find((a) => a.account_id === accountId);
  const isSyncable =
    !!currentAccount &&
    currentAccount.is_oauth === true &&
    ["youtube", "facebook", "instagram"].includes(currentAccount.platform);

  const posts = useQuery<{ items: SyncedPost[] }>({
    queryKey: ["synced-posts", accountId],
    queryFn: async () =>
      (await api.get(`/platforms/${accountId}/synced-posts`, {
        params: { limit: 25 },
      })).data,
    enabled: !!accountId,
  });

  const suggestions = useQuery<SyncSuggestions>({
    queryKey: ["sync-suggestions", accountId],
    queryFn: async () =>
      (await api.get(`/platforms/${accountId}/suggestions`)).data,
    enabled: !!accountId,
  });

  if (!accountId) return null;

  return (
    <DashboardLayout
      title="Channel Insights"
      subtitle="Your existing videos, comments, leads and complaints — AI-classified and ready to action."
      seoPath={`/platforms/${accountId}/insights`}
      seoDescription="Channel insights: top videos, hot leads, complaints and repeated questions from your synced YouTube channel."
    >
      <div className="mb-4">
        <Link to="/platforms" className="inline-flex items-center gap-1 text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]">
          <ArrowLeft className="h-4 w-4" /> Back to Platforms
        </Link>
      </div>

      <div className="sg-card p-4 mb-6">
        {isSyncable ? (
          <SyncControls accountId={accountId} />
        ) : (
          <div className="text-sm text-[color:var(--sg-muted)]" data-testid="sync-not-available">
            Sync is only available for OAuth-connected YouTube, Facebook or Instagram accounts.{" "}
            {currentAccount && !currentAccount.is_oauth && (
              <>This is a mock account added for testing. </>
            )}
            <Link to="/platforms" className="underline hover:text-[color:var(--sg-fg)]">
              Connect a real account →
            </Link>
          </div>
        )}
      </div>

      {/* Suggestions grid */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Hot leads */}
        <div className="sg-card p-5" data-testid="hot-leads-card">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="h-5 w-5 text-emerald-600" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Hot leads</h2>
          </div>
          {(suggestions.data?.hot_leads?.length ?? 0) === 0 ? (
            <EmptyState title="No leads yet" description="Sync your channel or wait for buying-intent comments." />
          ) : (
            <ul className="space-y-2">
              {suggestions.data!.hot_leads.map((l) => (
                <li key={l.lead_id} className="text-sm border border-[color:var(--sg-border)] rounded-md p-2.5 bg-white">
                  <div className="text-xs text-[color:var(--sg-muted)] mb-1">
                    <span className="font-semibold text-[color:var(--sg-fg)]">@{l.author_handle.replace(/^@/, "")}</span>
                    {" · "}score {(l.intent_score * 100).toFixed(0)}%
                  </div>
                  <div className="line-clamp-2">{l.original_comment}</div>
                  <div className="mt-1 text-xs text-[color:var(--sg-muted)] italic line-clamp-2">
                    Next: {l.suggested_next_step}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/leads">
            <Button variant="ghost" size="sm" className="mt-2 text-xs" data-testid="go-to-leads">
              Open Leads pipeline →
            </Button>
          </Link>
        </div>

        {/* Complaints */}
        <div className="sg-card p-5" data-testid="open-complaints-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Open complaints</h2>
          </div>
          {(suggestions.data?.open_complaints?.length ?? 0) === 0 ? (
            <EmptyState title="No complaints" description="Your audience is happy — nothing needs urgent attention." />
          ) : (
            <ul className="space-y-2">
              {suggestions.data!.open_complaints.map((c) => (
                <li key={c.complaint_id} className="text-sm border border-[color:var(--sg-border)] rounded-md p-2.5 bg-white">
                  <div className="text-xs text-[color:var(--sg-muted)] mb-1">
                    <span className="font-semibold text-[color:var(--sg-fg)]">@{c.author_handle.replace(/^@/, "")}</span>
                    {" · "}<span className={`sg-chip ${
                      c.severity === "high" ? "bg-rose-100 text-rose-700 border-rose-300"
                      : c.severity === "medium" ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-slate-100 text-slate-700 border-slate-200"
                    }`}>{c.severity}</span>
                  </div>
                  <div className="line-clamp-2">{c.source_text}</div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/complaints">
            <Button variant="ghost" size="sm" className="mt-2 text-xs" data-testid="go-to-complaints">
              Open Complaints tracker →
            </Button>
          </Link>
        </div>

        {/* Repeated questions */}
        <div className="sg-card p-5" data-testid="repeated-questions-card">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="h-5 w-5 text-sky-600" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Repeated questions</h2>
          </div>
          {(suggestions.data?.repeated_questions?.length ?? 0) === 0 ? (
            <EmptyState title="No repeated questions detected" description="Once similar questions show up 2+ times, they'll appear here." />
          ) : (
            <ul className="space-y-2">
              {suggestions.data!.repeated_questions.map((q, i) => (
                <li key={i} className="text-sm border border-[color:var(--sg-border)] rounded-md p-2.5 bg-white">
                  <div className="text-xs text-[color:var(--sg-muted)] mb-1">Asked {q.count} times</div>
                  <div className="line-clamp-2">{q.question}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Content ideas */}
        <div className="sg-card p-5" data-testid="content-ideas-card">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Content ideas</h2>
          </div>
          {(suggestions.data?.content_ideas?.length ?? 0) === 0 ? (
            <EmptyState title="Need a bit more signal" description="Sync more comments and questions to unlock content ideas." />
          ) : (
            <ul className="space-y-2">
              {suggestions.data!.content_ideas.map((idea, i) => (
                <li key={i} className="text-sm border border-[color:var(--sg-border)] rounded-md p-2.5 bg-white">
                  <div className="font-semibold">{idea.hook}</div>
                  <div className="text-xs text-[color:var(--sg-muted)] mt-1">
                    <span className="sg-chip bg-amber-50 text-amber-700 border-amber-200 mr-1">{idea.format}</span>
                    {idea.why}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Synced videos list */}
      <div className="sg-card p-5" data-testid="synced-videos-list">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-[color:var(--sg-fg)]" />
          <h2 className="font-display text-lg font-semibold tracking-tight">Your synced videos</h2>
        </div>
        {posts.isLoading ? (
          <div className="sg-skeleton h-40" />
        ) : (posts.data?.items?.length ?? 0) === 0 ? (
          <EmptyState
            title="No videos synced yet"
            description="Click Sync now above to pull existing videos and comments from your channel."
          />
        ) : (
          <div className="divide-y divide-[color:var(--sg-border)]">
            {posts.data!.items.map((v) => (
              <VideoRow key={v.post_id} v={v} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ChannelInsights;
